import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { Quotation, CreateQuotationData, QuotationItem, QuotationAttachedPdf, QuotationDocument, QuotationSource } from '../../types/quotation';
import toast from 'react-hot-toast';
import DateTimePicker from '../common/DateTimePicker';
import { uploadQuotationFile, deleteQuotationFile, fmtSize, MAX_QUOTATION_FILE_MB } from './quotationFiles';
import { PaperClipIcon, DocumentArrowUpIcon, XMarkIcon, ArrowTopRightOnSquareIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Product } from '../../types/product';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import { useQuotations } from '../../hooks/useQuotations';
import Modal from '../common/Modal';
import { DatePicker } from '../common/DatePicker';
import {
  BuildingOffice2Icon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  ShoppingCartIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { generateQuotationNumber } from '../../utils/formatters';
import { formatDocumentDate } from '../../utils/formatters';
import { Combobox, Portal } from '@headlessui/react';
import { ChevronUpDownIcon as ChevronUpDownIconSolid } from '@heroicons/react/20/solid';
import { useLanguage } from '../../contexts/LanguageContext';

interface QuotationFormSimpleProps {
  /** opts.send: mentés után a küldés/megosztás ablak nyíljon meg az ügyfélnek */
  onSubmit: (data: CreateQuotationData, opts?: { send?: boolean }) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialData?: Quotation;
}

interface ProductSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct: (product: Product) => void;
  products: Product[];
}

const ProductSearchModal: React.FC<ProductSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectProduct,
  products
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    
    const query = searchQuery.toLowerCase();
    return products.filter(product =>
      product.name.toLowerCase().includes(query) ||
      product.code.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query)
    );
  }, [products, searchQuery]);

  const handleProductSelect = (product: Product) => {
    onSelectProduct(product);
    onClose();
    setSearchQuery('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('quotations.form.productSearch.title')}
      size="4xl"
    >
      <div className="p-6">
        {/* Search Input */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder={t('quotations.form.productSearch.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pl-10 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        {/* Products List */}
        <div className="max-h-[32rem] overflow-y-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <ShoppingCartIcon className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
              <p>{searchQuery ? t('quotations.form.productSearch.noResults') : t('quotations.form.productSearch.noProducts')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  className="flex items-center p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer transition-all"
                >
                  <div className="p-2 rounded bg-gradient-to-br from-green-100 to-teal-200 dark:from-green-800 dark:to-teal-700 mr-3 flex-shrink-0">
                    <ShoppingCartIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {product.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                        {product.code}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                      {product.description}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {product.unit} • {product.taxRate || '27%'} ÁFA
                      </div>
                      {product.netPrice && (
                        <div className="text-sm font-medium text-green-600 dark:text-green-400">
                          {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(product.netPrice)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const QuotationFormSimple: React.FC<QuotationFormSimpleProps> = ({ onSubmit, onCancel, isOpen, initialData }) => {
  const { t } = useLanguage();
  const { organizations } = useOrganizations();
  const { customers } = useCustomers();
  const { products } = useProducts();
  const { quotations } = useQuotations();
  const [orgQuery, setOrgQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);

  const lastQuotationNumber = quotations
    .map(q => q.number)
    .sort()
    .reverse()[0];

  const { user } = useAuth();
  const activeOrgId = (user as any)?.organizationId || '';

  const defaultValues: CreateQuotationData = {
    status: 'draft',
    number: generateQuotationNumber(lastQuotationNumber, 'Minta', [], ''),
    organizationId: activeOrgId,
    customerId: '',
    quotationNumber: '',
    companyName: '',
    companyAddress: '',
    taxNumber: '',
    registrationNumber: '',
    representativeName: '',
    contactName: '',
    date: new Date().toISOString().split('T')[0],
    // alapértelmezett érvényesség: 8 nap
    validUntil: new Date(Date.now() + 8 * 86400000).toISOString().split('T')[0],
    paymentDue: '15',
    
    // Basic pricing information
    subtotal: 0,
    taxAmount: 0,
    totalAmount: 0,
    currency: 'HUF',
    
    documents: [],
    items: [],
    publicToken: '',
    notes: ''
  };

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<CreateQuotationData>({
    defaultValues
  });

  // Initialize form when modal opens
  // A tételeket lokális state-ben is tartjuk: a react-hook-form setValue tömbnél
  // nem mindig vált ki újrarenderelést, ezért a lista innen renderelődik.
  const [items, setItemsState] = useState<QuotationItem[]>([]);
  // Ajánlat módja: itt összeállított tételes, vagy máshol készített, csatolt PDF
  const [source, setSource] = useState<QuotationSource>('builder');
  const [attachedPdf, setAttachedPdf] = useState<QuotationAttachedPdf | null>(null);
  const [docs, setDocs] = useState<QuotationDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const sendAfterRef = React.useRef(false);
  const [pdfNet, setPdfNet] = useState('');
  const [pdfVat, setPdfVat] = useState(27);
  const setItems = (next: QuotationItem[]) => {
    setItemsState(next);
    setValue('items', next, { shouldDirty: true });
    calculateTotals(next);
  };

  useEffect(() => {
    if (isOpen) {
      const formData: any = { ...(initialData || defaultValues) };
      // a dátumokat egységesen 'YYYY-MM-DD' szövegként tartjuk az űrlapban
      for (const k of ['date', 'validUntil']) {
        const v = formData[k];
        if (v && typeof v !== 'string') {
          const d = typeof v?.toDate === 'function' ? v.toDate() : v instanceof Date ? v : typeof v?.seconds === 'number' ? new Date(v.seconds * 1000) : new Date(v);
          formData[k] = d instanceof Date && !isNaN(d.getTime()) ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
        }
      }
      reset(formData);
      const num = (v: any) => (typeof v === 'string' ? parseFloat(v) || 0 : Number(v) || 0);
      setItemsState(Array.isArray(formData.items)
        ? formData.items.map((it: any) => ({
            ...it,
            quantity: num(it.quantity) || 1,
            unitPrice: num(it.unitPrice ?? it.price),
            price: num(it.price ?? it.unitPrice),
            taxRate: num(it.taxRate),
            discount: num(it.discount),
          }))
        : []);
      const src: QuotationSource = (formData as any).source === 'pdf' ? 'pdf' : 'builder';
      setSource(src);
      setAttachedPdf((formData as any).attachedPdf || null);
      setDocs(Array.isArray(formData.documents) ? formData.documents : []);
      const sub = num((formData as any).subtotal);
      setPdfNet(src === 'pdf' && sub ? String(Math.round(sub)) : '');
      setPdfVat(src === 'pdf' && sub ? Math.round((num((formData as any).taxAmount) / sub) * 100) : 27);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData, reset]);

  // Csatolt PDF-es ajánlatnál az összeg kézzel adható meg
  useEffect(() => {
    if (source !== 'pdf') return;
    const net = Math.max(0, Number(String(pdfNet).replace(/\s/g, '').replace(',', '.')) || 0);
    const tax = Math.round(net * pdfVat) / 100;
    setValue('subtotal', net);
    setValue('taxAmount', tax);
    setValue('totalAmount', net + tax);
  }, [source, pdfNet, pdfVat, setValue]);

  const switchSource = (next: QuotationSource) => {
    setSource(next);
    if (next === 'builder') calculateTotals(items);
  };

  const pickPdf = async (file?: File | null) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) { toast.error('Csak PDF fájl tölthető fel ajánlatként.'); return; }
    setUploading(true);
    try {
      const up = await uploadQuotationFile(file, watch('organizationId') || activeOrgId);
      if (attachedPdf?.path && attachedPdf.path !== up.path) deleteQuotationFile(attachedPdf.path);
      setAttachedPdf(up);
      toast.success('PDF feltöltve');
    } catch (e: any) { console.error(e); toast.error(e?.message || 'A feltöltés nem sikerült'); }
    finally { setUploading(false); }
  };

  const addDocs = async (files?: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const added: QuotationDocument[] = [];
      for (const f of Array.from(files)) {
        const up = await uploadQuotationFile(f, watch('organizationId') || activeOrgId);
        added.push({ id: up.path, name: up.name, originalName: up.name, url: up.url, path: up.path, size: up.size, uploadedAt: new Date(), type: 'other' });
      }
      setDocs(d => [...d, ...added]);
      toast.success(added.length === 1 ? 'Melléklet csatolva' : `${added.length} melléklet csatolva`);
    } catch (e: any) { console.error(e); toast.error(e?.message || 'A feltöltés nem sikerült'); }
    finally { setUploading(false); }
  };

  const removeDoc = (d: QuotationDocument) => {
    setDocs(list => list.filter(x => x.id !== d.id));
    // a fájlt csak akkor töröljük, ha még nem volt elmentett ajánlat része
    if (!initialData?.documents?.some(x => x.id === d.id)) deleteQuotationFile(d.path);
  };

  // Érvényesség gyorsgombok: a kelte + n nap
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  // A dátum lehet 'YYYY-MM-DD', Date vagy Firestore Timestamp (pl. duplikált / régi ajánlatnál)
  const fromYmd = (v?: any): Date | undefined => {
    if (!v) return undefined;
    if (typeof v === 'string') {
      const m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      const d = new Date(v); return isNaN(d.getTime()) ? undefined : d;
    }
    const d = typeof v?.toDate === 'function' ? v.toDate() : v instanceof Date ? v : typeof v?.seconds === 'number' ? new Date(v.seconds * 1000) : new Date(v);
    return d instanceof Date && !isNaN(d.getTime()) ? d : undefined;
  };
  const setValidDays = (n: number) => {
    const base = fromYmd(watch('date')) || new Date();
    setValue('validUntil', ymd(new Date(base.getFullYear(), base.getMonth(), base.getDate() + n)), { shouldDirty: true });
  };
  const validDiff = (() => { const a = fromYmd(watch('date')), b = fromYmd(watch('validUntil')); return a && b ? Math.round((b.getTime() - a.getTime()) / 86400000) : null; })();

  const selectedOrgId = watch('organizationId');
  const selectedOrg = organizations.find(org => org.id === selectedOrgId);

  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org =>
      org.name.toLowerCase().includes(orgQuery.toLowerCase())
    );
  }, [organizations, orgQuery]);

  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const searchStr = customer.type === 'organization'
        ? customer.companyName?.toLowerCase()
        : `${customer.lastName} ${customer.firstName}`.toLowerCase();
      
      return searchStr?.includes(customerQuery.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerQuery.toLowerCase());
    });
  }, [customers, customerQuery]);

  const selectedCustomerId = watch('customerId');
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  // Auto-fill customer data when selected
  useEffect(() => {
    if (selectedCustomer && selectedCustomer.type === 'organization') {
      setValue('companyName', selectedCustomer.companyName || '');
      setValue('taxNumber', selectedCustomer.taxNumber || '');
      setValue('registrationNumber', selectedCustomer.registrationNumber || '');
      
      if (selectedCustomer.address) {
        const addressParts = [];
        if (selectedCustomer.address.street) addressParts.push(selectedCustomer.address.street);
        if (selectedCustomer.address.city) addressParts.push(selectedCustomer.address.city);
        if (selectedCustomer.address.postalCode) addressParts.push(selectedCustomer.address.postalCode);
        if (selectedCustomer.address.country) addressParts.push(selectedCustomer.address.country);
        
        const fullAddress = addressParts.join(', ');
        setValue('companyAddress', fullAddress);
      }
      
      if (selectedCustomer.contactPerson && selectedCustomer.contactPerson.name) {
        setValue('contactName', selectedCustomer.contactPerson.name);
      }
    }
  }, [selectedCustomerId, selectedCustomer, setValue]);

  // Product items management
  const handleAddItem = () => {
    const newItem: QuotationItem = {
      productId: '',
      productName: '',
      productCode: '',
      quantity: 1,
      unitPrice: 0,
      price: 0,
      taxRate: 27,
      lineTotal: 0,
      lineTotalWithTax: 0,
      currency: 'HUF'
    };
    
    setItems([...items, newItem]);
  };

  const handleAddProductFromSearch = () => {
    setIsProductSearchOpen(true);
  };

  const handleProductSelect = (product: Product) => {
    const netPrice = Number(product.netPrice || 0);
    const taxRate = product.taxRate === '27%' ? 27 : product.taxRate === '18%' ? 18 : product.taxRate === '5%' ? 5 : 27;
    
    const newItem: QuotationItem = {
      productId: product.id,
      productName: product.name,
      productCode: product.code,
      quantity: 1,
      unitPrice: netPrice,
      price: netPrice,
      taxRate: taxRate,
      lineTotal: netPrice,
      lineTotalWithTax: Number((netPrice * (1 + taxRate / 100)).toFixed(2)),
      currency: 'HUF'
    };
    
    setItems([...items, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotals = (items: QuotationItem[]) => {
    const subtotal = items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
    const taxAmount = items.reduce((sum, item) => sum + (Number(item.lineTotalWithTax || 0) - Number(item.lineTotal || 0)), 0);
    const totalAmount = subtotal + taxAmount;
    
    setValue('subtotal', subtotal);
    setValue('taxAmount', taxAmount);
    setValue('totalAmount', totalAmount);
  };

  const handleItemChange = (index: number, field: keyof QuotationItem, value: any) => {
    const updatedItems = [...items];
    
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    
    // Calculate line totals with improved logic
    const item = updatedItems[index];
    if (field === 'quantity' || field === 'unitPrice' || field === 'price' || field === 'taxRate' || field === 'discount') {
      // Biztosítjuk, hogy számként kezeljük az értékeket
      const basePrice = Number(item.unitPrice || item.price || 0);
      const quantity = Number(item.quantity || 1);
      const discount = Number(item.discount || 0);
      const taxRate = Number(item.taxRate || 0);
      
      // Sync price with unitPrice
      item.price = basePrice;
      
      // Calculate with discount
      const discountAmount = basePrice * (discount / 100);
      const discountedPrice = basePrice - discountAmount;
      
      // Calculate totals - biztosítjuk, hogy számként tároljuk
      item.lineTotal = Number((quantity * discountedPrice).toFixed(2));
      item.lineTotalWithTax = Number((item.lineTotal * (1 + taxRate / 100)).toFixed(2));
    }
    
    setItems(updatedItems);
  };

  const handleFormSubmit = (data: CreateQuotationData) => {
    if (!data.validUntil) { toast.error('Add meg az érvényességet.'); return; }
    if (uploading) { toast.error('Várd meg, amíg a feltöltés befejeződik.'); return; }
    (data as any).source = source;
    data.documents = docs;
    if (source === 'pdf') {
      if (!attachedPdf) { toast.error('Töltsd fel az ajánlat PDF-jét.'); return; }
      (data as any).attachedPdf = attachedPdf;
      data.items = [];
      const net = Math.max(0, Number(String(pdfNet).replace(/\s/g, '').replace(',', '.')) || 0);
      const tax = Math.round(net * pdfVat) / 100;
      data.subtotal = net; data.taxAmount = tax; data.totalAmount = net + tax;
      onSubmit(data, { send: sendAfterRef.current });
      return;
    }
    (data as any).attachedPdf = null;
    // Ensure items have proper calculations
    if (data.items && data.items.length > 0) {
      data.items = data.items.map(item => {
        const basePrice = Number(item.unitPrice || item.price || 0);
        const quantity = Number(item.quantity || 1);
        const discount = Number(item.discount || 0);
        const taxRate = Number(item.taxRate || 0);
        
        // Calculate with discount
        const discountAmount = basePrice * (discount / 100);
        const discountedPrice = basePrice - discountAmount;
        
        return {
          ...item,
          price: basePrice,
          lineTotal: Number((quantity * discountedPrice).toFixed(2)),
          lineTotalWithTax: Number((quantity * discountedPrice * (1 + taxRate / 100)).toFixed(2))
        };
      });
      
      calculateTotals(data.items);
    }
    
    onSubmit(data, { send: sendAfterRef.current });
  };

  const currentItems = items;

  const fmt = new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', maximumFractionDigits: 0 });
  const inputCls = "w-full h-9 border rounded-lg px-2.5 text-sm tabular-nums";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? t('quotations.form.title.edit') : t('quotations.form.title.create')}
      size="7xl"
      noPadding
    >
      <form noValidate onSubmit={handleSubmit(handleFormSubmit)} className="lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:h-full">

        {/* ===== Bal: tételek + megjegyzés ===== */}
        <div className="min-w-0 p-4 sm:p-6 space-y-5 pb-40 lg:pb-6 lg:overflow-y-auto">

          {/* Ajánlat módja */}
          <div className="flex rounded-lg border border-[var(--crm-border)] bg-[var(--crm-surface)] p-0.5 w-full sm:w-auto sm:inline-flex">
            {([['builder', 'Tételes ajánlat itt'], ['pdf', 'Kész ajánlat PDF-ben']] as [QuotationSource, string][]).map(([id, label]) => (
              <button key={id} type="button" onClick={() => switchSource(id)}
                className={`flex-1 sm:flex-none px-3 h-8 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap ${source === id ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'}`}>
                {label}
              </button>
            ))}
          </div>

          {source === 'pdf' && (
            <section className="crm-card overflow-hidden">
              <header className="px-4 py-3 border-b border-[var(--crm-border)]">
                <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Az ajánlat PDF-je és összege</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Wordben vagy más programban készült ajánlat. Az ügyfél ezt látja és tölti le a megosztott linken.</p>
              </header>
              <div className="p-4 space-y-4">
                {attachedPdf ? (
                  <div className="flex items-center gap-3 rounded-lg border border-[var(--crm-border)] p-3">
                    <DocumentTextIcon className="w-8 h-8 text-rose-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{attachedPdf.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{fmtSize(attachedPdf.size)}</div>
                    </div>
                    <a href={attachedPdf.url} target="_blank" rel="noopener noreferrer" className="crm-btn crm-btn-ghost h-8 text-xs"><ArrowTopRightOnSquareIcon className="w-4 h-4" />Megnyitás</a>
                    <label className="crm-btn crm-btn-secondary h-8 text-xs cursor-pointer">Csere<input type="file" accept="application/pdf,.pdf" className="hidden" onChange={e => { pickPdf(e.target.files?.[0]); e.target.value = ''; }} /></label>
                  </div>
                ) : (
                  <label
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); pickPdf(e.dataTransfer.files?.[0]); }}
                    className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[var(--crm-border)] py-8 cursor-pointer hover:border-primary-400 hover:bg-primary-50/40 dark:hover:bg-primary-900/10">
                    <DocumentArrowUpIcon className="w-8 h-8 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{uploading ? 'Feltöltés…' : 'PDF kiválasztása vagy ide húzása'}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">legfeljebb {MAX_QUOTATION_FILE_MB} MB</span>
                    <input type="file" accept="application/pdf,.pdf" className="hidden" disabled={uploading} onChange={e => { pickPdf(e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <label className="block sm:col-span-2">
                    <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nettó összeg (Ft) *</span>
                    <input value={pdfNet} onChange={e => setPdfNet(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="pl. 490000" className={inputCls + ' text-right'} />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">ÁFA</span>
                    <select value={pdfVat} onChange={e => setPdfVat(Number(e.target.value))} className="w-full h-9 border rounded-lg pl-2 pr-6 text-sm">
                      <option value={27}>27%</option><option value={18}>18%</option><option value={5}>5%</option><option value={0}>0% / AAM</option>
                    </select>
                  </label>
                </div>
              </div>
            </section>
          )}

          {/* Tételek */}
          {source === 'builder' && (
          <section className="crm-card overflow-hidden">
            <header className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-[var(--crm-border)]">
              <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{t('quotations.form.sections.items.title')}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{currentItems.length} tétel</span>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={handleAddItem} className="crm-btn crm-btn-secondary h-8 text-xs">
                  <PlusIcon className="w-3.5 h-3.5" />Üres sor
                </button>
                <button type="button" onClick={handleAddProductFromSearch} className="crm-btn crm-btn-primary h-8 text-xs">
                  <MagnifyingGlassIcon className="w-3.5 h-3.5" />Termék hozzáadása
                </button>
              </div>
            </header>

            {currentItems.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center mx-auto mb-3">
                  <ShoppingCartIcon className="w-5 h-5" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Még nincs tétel az árajánlatban.</p>
                <button type="button" onClick={handleAddProductFromSearch} className="mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">+ Termék hozzáadása</button>
              </div>
            ) : (
              <>
                {/* Asztali táblázat */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="crm-table-raw w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400 bg-[var(--crm-bg)]">
                        <th className="text-left font-semibold px-4 py-2 w-8">#</th>
                        <th className="text-left font-semibold px-2 py-2">Termék</th>
                        <th className="text-right font-semibold px-2 py-2 w-24">Menny.</th>
                        <th className="text-right font-semibold px-2 py-2 w-32">Egységár</th>
                        <th className="text-right font-semibold px-2 py-2 w-20">Kedv. %</th>
                        <th className="text-right font-semibold px-2 py-2 w-20">ÁFA</th>
                        <th className="text-right font-semibold px-2 py-2 w-32">Nettó</th>
                        <th className="text-right font-semibold px-2 py-2 w-32">Bruttó</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--crm-border)]">
                      {currentItems.map((item, index) => (
                        <tr key={index} className="align-top">
                          <td className="px-4 py-2 text-gray-400 tabular-nums pt-4">{index + 1}</td>
                          <td className="px-2 py-2 min-w-[220px]">
                            <input
                              type="text"
                              value={item.productName}
                              onChange={(e) => handleItemChange(index, 'productName', e.target.value)}
                              className={inputCls + ' font-medium'}
                              placeholder="Termék neve"
                            />
                            {item.productCode && <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 font-mono">{item.productCode}</div>}
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} className={inputCls + ' text-right'} />
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" min="0" step="1" value={item.unitPrice || item.price || 0} onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} className={inputCls + ' text-right'} />
                          </td>
                          <td className="px-2 py-2">
                            <input type="number" min="0" max="100" step="0.5" value={item.discount || 0} onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value) || 0)} className={inputCls + ' text-right'} />
                          </td>
                          <td className="px-2 py-2">
                            <select value={item.taxRate} onChange={(e) => handleItemChange(index, 'taxRate', parseFloat(e.target.value))} className="w-full h-9 border rounded-lg pl-2 pr-6 text-sm">
                              <option value={27}>27%</option>
                              <option value={18}>18%</option>
                              <option value={5}>5%</option>
                              <option value={0}>0%</option>
                            </select>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums pt-4 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmt.format(item.lineTotal || 0)}</td>
                          <td className="px-2 py-2 text-right tabular-nums pt-4 font-medium text-gray-900 dark:text-gray-50 whitespace-nowrap">{fmt.format(item.lineTotalWithTax || 0)}</td>
                          <td className="px-2 py-2 pt-3">
                            <button type="button" onClick={() => handleRemoveItem(index)} className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10" title="Tétel törlése">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobil kártyák */}
                <div className="md:hidden divide-y divide-[var(--crm-border)]">
                  {currentItems.map((item, index) => (
                    <div key={index} className="p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-xs text-gray-400 tabular-nums mt-2.5 w-5">{index + 1}.</span>
                        <input type="text" value={item.productName} onChange={(e) => handleItemChange(index, 'productName', e.target.value)} className={inputCls + ' font-medium flex-1'} placeholder="Termék neve" />
                        <button type="button" onClick={() => handleRemoveItem(index)} className="p-2 rounded-md text-gray-400 hover:text-red-600" title="Törlés"><TrashIcon className="w-4 h-4" /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pl-7">
                        <label className="text-[11px] text-gray-500 dark:text-gray-400">Mennyiség
                          <input type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} className={inputCls + ' mt-0.5'} />
                        </label>
                        <label className="text-[11px] text-gray-500 dark:text-gray-400">Egységár
                          <input type="number" min="0" step="1" value={item.unitPrice || item.price || 0} onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)} className={inputCls + ' mt-0.5'} />
                        </label>
                        <label className="text-[11px] text-gray-500 dark:text-gray-400">Kedvezmény %
                          <input type="number" min="0" max="100" step="0.5" value={item.discount || 0} onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value) || 0)} className={inputCls + ' mt-0.5'} />
                        </label>
                        <label className="text-[11px] text-gray-500 dark:text-gray-400">ÁFA
                          <select value={item.taxRate} onChange={(e) => handleItemChange(index, 'taxRate', parseFloat(e.target.value))} className="w-full h-9 border rounded-lg px-2 text-sm mt-0.5">
                            <option value={27}>27%</option><option value={18}>18%</option><option value={5}>5%</option><option value={0}>0%</option>
                          </select>
                        </label>
                      </div>
                      <div className="flex justify-between pl-7 text-sm tabular-nums">
                        <span className="text-gray-500 dark:text-gray-400">Nettó {fmt.format(item.lineTotal || 0)}</span>
                        <span className="font-medium text-gray-900 dark:text-gray-50">Bruttó {fmt.format(item.lineTotalWithTax || 0)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
          )}

          {/* Megjegyzés */}
          <section className="crm-card">
            <header className="px-4 py-3 border-b border-[var(--crm-border)]">
              <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">{source === 'pdf' ? 'Kísérő szöveg / megjegyzés' : t('quotations.form.sections.notes.title')}</h3>
            </header>
            <div className="p-4">
              <textarea {...register('notes')} rows={source === 'pdf' ? 6 : 4} className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder={source === 'pdf' ? 'Az ügyfélnek a linken megjelenő szöveg – pl. rövid összefoglaló, feltételek, kapcsolattartó…' : 'Az ajánlat alján megjelenő megjegyzés – feltételek, szállítás, garancia…'} />
            </div>
          </section>

          {/* Mellékletek */}
          <section className="crm-card">
            <header className="flex items-center gap-2 px-4 py-3 border-b border-[var(--crm-border)]">
              <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Mellékletek</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{docs.length}</span>
              <label className={`ml-auto crm-btn crm-btn-secondary h-8 text-xs cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                <PaperClipIcon className="w-3.5 h-3.5" />{uploading ? 'Feltöltés…' : 'Fájl csatolása'}
                <input type="file" multiple className="hidden" onChange={e => { addDocs(e.target.files); e.target.value = ''; }} />
              </label>
            </header>
            <div className="p-4" onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); addDocs(e.dataTransfer.files); }}>
              {docs.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">Műszaki leírás, prospektus, referencia, képek… Ide is húzhatod a fájlokat (egyenként legfeljebb {MAX_QUOTATION_FILE_MB} MB). Az ügyfél a megosztott linken letöltheti őket.</p>
              ) : (
                <ul className="divide-y divide-[var(--crm-border)]">
                  {docs.map(d => (
                    <li key={d.id} className="flex items-center gap-3 py-2 text-sm">
                      <PaperClipIcon className="w-4 h-4 text-gray-400 shrink-0" />
                      <a href={d.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate hover:underline">{d.name}</a>
                      <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{fmtSize(d.size)}</span>
                      <button type="button" onClick={() => removeDoc(d)} className="p-1 rounded-md text-gray-400 hover:text-red-600" title="Eltávolítás"><XMarkIcon className="w-4 h-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>

        {/* ===== Jobb: ügyfél, dátumok, összesítő ===== */}
        <aside className="lg:border-l border-[var(--crm-border)] bg-[var(--crm-bg)] lg:overflow-y-auto">
          <div className="p-4 sm:p-6 space-y-5">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('quotations.form.fields.organization')} *</label>
                  <Combobox
                    value={watch('organizationId')}
                    onChange={(orgId) => setValue('organizationId', orgId)}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full h-9 border rounded-lg pl-3 pr-8 text-sm"
                        onChange={event => setOrgQuery(event.target.value)}
                        displayValue={(orgId: string) => {
                          const org = organizations.find(o => o.id === orgId);
                          return org ? org.name : '';
                        }}
                        placeholder={t('common.placeholders.selectOrganization')}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </Combobox.Button>
                      <Combobox.Options className="absolute z-[9999] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none">
                        {filteredOrganizations.map(org => (
                          <Combobox.Option
                            key={org.id}
                            value={org.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100' : 'text-gray-800 dark:text-gray-200'}`
                            }
                          >
                            {org.name}
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('quotations.form.fields.customer')} *</label>
                  <Combobox
                    value={watch('customerId')}
                    onChange={(customerId) => setValue('customerId', customerId)}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full h-9 border rounded-lg pl-3 pr-8 text-sm"
                        onChange={event => setCustomerQuery(event.target.value)}
                        displayValue={(customerId: string) => {
                          const customer = customers.find(c => c.id === customerId);
                          if (!customer) return '';
                          return customer.type === 'organization'
                            ? customer.companyName || ''
                            : `${customer.lastName} ${customer.firstName}`;
                        }}
                        placeholder={t('common.placeholders.selectCustomer')}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </Combobox.Button>
                      <Combobox.Options className="absolute z-[9999] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none">
                        {filteredCustomers.map(customer => (
                          <Combobox.Option
                            key={customer.id}
                            value={customer.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100' : 'text-gray-800 dark:text-gray-200'}`
                            }
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`p-1 rounded ${customer.type === 'organization' ? 'bg-sky-50 dark:bg-sky-500/15' : 'bg-primary-50 dark:bg-primary-500/15'}`}>
                                {customer.type === 'organization' ? (
                                  <BuildingOffice2Icon className="h-4 w-4 text-sky-700 dark:text-sky-300" />
                                ) : (
                                  <UserIcon className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium">
                                  {customer.type === 'organization'
                                    ? customer.companyName
                                    : `${customer.lastName} ${customer.firstName}`}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {customer.email}
                                </div>
                              </div>
                            </div>
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('quotations.form.fields.date')} *</label>
                  <DateTimePicker showTime={false} value={fromYmd(watch('date'))} onChange={d => setValue('date', d ? ymd(d) : '', { shouldDirty: true })} placeholder="Kelte" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('quotations.form.fields.validUntil')} *</label>
                  <DateTimePicker showTime={false} value={fromYmd(watch('validUntil'))} minDate={fromYmd(watch('date'))} onChange={d => setValue('validUntil', d ? ymd(d) : '', { shouldDirty: true })} placeholder="Érvényes eddig" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-gray-500 dark:text-gray-400 mr-0.5">Érvényes:</span>
                {[1, 2, 4, 8, 15].map(n => (
                  <button key={n} type="button" onClick={() => setValidDays(n)}
                    className={`h-7 px-2.5 rounded-full border text-xs font-medium tabular-nums ${validDiff === n ? 'bg-primary-600 border-primary-600 text-white' : 'border-[var(--crm-border)] text-gray-600 dark:text-gray-300 hover:border-gray-400'}`}>
                    {n} nap
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Fizetési határidő (nap)</label>
                <input type="number" min="1" max="365" {...register('paymentDue')} className="w-full h-9 border rounded-lg px-2.5 text-sm tabular-nums" placeholder="15" />
              </div>
            </div>

            {/* Összesítő – asztalon itt, mobilon az alsó sávban */}
            <div className="hidden lg:block crm-card p-4 space-y-2 tabular-nums">
              <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">{t('quotations.fields.subtotal')}</span><span className="text-gray-900 dark:text-gray-100">{fmt.format(watch('subtotal') || 0)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-gray-400">ÁFA</span><span className="text-gray-900 dark:text-gray-100">{fmt.format(watch('taxAmount') || 0)}</span></div>
              <div className="flex justify-between items-baseline pt-2 border-t border-[var(--crm-border)]"><span className="text-sm font-medium text-gray-900 dark:text-gray-50">Bruttó összesen</span><span className="text-xl font-semibold text-gray-900 dark:text-gray-50">{fmt.format(watch('totalAmount') || 0)}</span></div>
            </div>

            <div className="hidden lg:flex flex-col gap-2">
              <button type="submit" onClick={() => { sendAfterRef.current = true; }} className="crm-btn crm-btn-primary w-full" title="Mentés, majd az ügyfélnek küldés e-mailben (megosztási linkkel)">
                <PaperAirplaneIcon className="w-4 h-4" />Mentés és küldés az ügyfélnek
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={onCancel} className="crm-btn crm-btn-secondary flex-1">{t('common.buttons.cancel')}</button>
                <button type="submit" onClick={() => { sendAfterRef.current = false; }} className="crm-btn crm-btn-secondary flex-1">{initialData ? t('common.buttons.save') : 'Csak mentés'}</button>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobil / tablet: rögzített összesítő + gombok */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-10 bg-[var(--crm-surface)] border-t border-[var(--crm-border)] p-3 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-baseline justify-between mb-2 tabular-nums">
            <span className="text-xs text-gray-500 dark:text-gray-400">Nettó {fmt.format(watch('subtotal') || 0)} · ÁFA {fmt.format(watch('taxAmount') || 0)}</span>
            <span className="text-lg font-semibold text-gray-900 dark:text-gray-50">{fmt.format(watch('totalAmount') || 0)}</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} className="crm-btn crm-btn-secondary h-11 flex-1">{t('common.buttons.cancel')}</button>
            <button type="submit" onClick={() => { sendAfterRef.current = false; }} className="crm-btn crm-btn-secondary h-11 flex-1">Mentés</button>
            <button type="submit" onClick={() => { sendAfterRef.current = true; }} className="crm-btn crm-btn-primary h-11 flex-[1.4]"><PaperAirplaneIcon className="w-4 h-4" />Küldés</button>
          </div>
        </div>
      </form>

      {isProductSearchOpen && (
        <ProductSearchModal
          isOpen={isProductSearchOpen}
          onClose={() => setIsProductSearchOpen(false)}
          onSelectProduct={handleProductSelect}
          products={products}
        />
      )}
    </Modal>
  );
};

export default QuotationFormSimple;
